import PizZip from 'pizzip';

interface AttachmentImage {
  blob: Blob;
  name: string;
  type: string;
  title: string;
}

/**
 * Appends images to the end of a DOCX document (zip).
 * Adds each image on a new page, matching the document's page size.
 * Uses PURE string manipulation to avoid valid XML namespace issues with DOMParser in browsers.
 */
export async function appendAttachmentsToZip(
  zip: PizZip,
  attachments: AttachmentImage[]
): Promise<PizZip> {
  console.log('Starting appendAttachmentsToZip (Pure String Version)...');
  if (attachments.length === 0) return zip;

  const docXmlPath = 'word/document.xml';
  const relsPath = 'word/_rels/document.xml.rels';
  const contentTypesPath = '[Content_Types].xml';

  const docFile = zip.file(docXmlPath);
  const relsFile = zip.file(relsPath);
  const contentTypesFile = zip.file(contentTypesPath);

  if (!docFile || !relsFile || !contentTypesFile) {
    throw new Error('Invalid DOCX structure: missing core XML files');
  }

  let docXml = docFile.asText();
  let relsXml = relsFile.asText();
  let contentTypesXml = contentTypesFile.asText();

  // 1. Get Page Size (Regex)
  let pageWidth = 11906; // A4 default
  let pageHeight = 16838;
  let marginX = 1440 * 2;
  let marginY = 1440 * 2;

  // Try to extract page dimensions from the last sectPr
  const pgSzMatch = docXml.match(/<w:pgSz[^>]*w:w="(\d+)"[^>]*w:h="(\d+)"/g);
  if (pgSzMatch && pgSzMatch.length > 0) {
    const lastMatch = pgSzMatch[pgSzMatch.length - 1];
    const wMatch = lastMatch.match(/w:w="(\d+)"/);
    const hMatch = lastMatch.match(/w:h="(\d+)"/);
    if (wMatch) pageWidth = parseInt(wMatch[1], 10);
    if (hMatch) pageHeight = parseInt(hMatch[1], 10);
  }

  const pgMarMatch = docXml.match(/<w:pgMar[^>]*>/g);
  if (pgMarMatch && pgMarMatch.length > 0) {
    const lastMar = pgMarMatch[pgMarMatch.length - 1];
    const left = parseInt(lastMar.match(/w:left="(\d+)"/)?.[1] || '1440', 10);
    const right = parseInt(lastMar.match(/w:right="(\d+)"/)?.[1] || '1440', 10);
    const top = parseInt(lastMar.match(/w:top="(\d+)"/)?.[1] || '1440', 10);
    const bottom = parseInt(lastMar.match(/w:bottom="(\d+)"/)?.[1] || '1440', 10);
    marginX = left + right;
    marginY = top + bottom;
  }

  const contentWidthTwips = pageWidth - marginX;
  const contentHeightTwips = pageHeight - marginY;
  const maxWEmus = contentWidthTwips * 635;
  const maxHEmus = contentHeightTwips * 635;

  // 2. Find next available Relationship ID (Regex)
  let maxId = 0;
  // Match Id="rIdX"
  const rIdMatches = relsXml.match(/Id="rId(\d+)"/g);
  if (rIdMatches) {
    for (const match of rIdMatches) {
      const num = parseInt(match.match(/Id="rId(\d+)"/)?.[1] || '0', 10);
      if (num > maxId) maxId = num;
    }
  }

  // Also check single quote just in case
  const rIdMatchesSingle = relsXml.match(/Id='rId(\d+)'/g);
  if (rIdMatchesSingle) {
    for (const match of rIdMatchesSingle) {
      const num = parseInt(match.match(/Id='rId(\d+)'/)?.[1] || '0', 10);
      if (num > maxId) maxId = num;
    }
  }

  let newContentXml = '';

  // 3. Process Attachments
  for (const att of attachments) {
    maxId++;
    const rId = `rId${maxId}`;
    const ext = att.type.split('/')[1] || 'png';
    const imgFileName = `image_${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${ext}`;

    // Add file to zip
    zip.file(`word/media/${imgFileName}`, await att.blob.arrayBuffer());

    // Add relationship (String injection)
    // Find closing tag </Relationships>
    const relsClosingTag = '</Relationships>';
    const relsInsertIndex = relsXml.lastIndexOf(relsClosingTag);
    // Use standard namespace for image relationship
    const newRelXml = `<Relationship Id="${rId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="media/${imgFileName}"/>`;

    if (relsInsertIndex !== -1) {
      relsXml = relsXml.slice(0, relsInsertIndex) + newRelXml + relsXml.slice(relsInsertIndex);
    } else {
      // Should not happen in valid docx
      console.error('Could not find </Relationships> closing tag');
    }

    // Calculate dimensions
    const dimensions = await getImageDimensions(att.blob);
    let widthEmus = dimensions.width * 9525;
    let heightEmus = dimensions.height * 9525;

    // Scale
    const ratio = widthEmus / heightEmus;
    if (widthEmus > maxWEmus) {
      widthEmus = maxWEmus;
      heightEmus = widthEmus / ratio;
    }
    if (heightEmus > maxHEmus) {
      heightEmus = maxHEmus;
      widthEmus = heightEmus * ratio;
    }

    const w = Math.floor(widthEmus);
    const h = Math.floor(heightEmus);

    // Generate XML Strings
    // Note: We use string injection for document.xml to avoid namespace complexity with DOMParser on the main body
    const pageBreakXml = `<w:p><w:pPr><w:br w:type="page"/><w:jc w:val="center"/></w:pPr><w:r><w:rPr><w:b/><w:sz w:val="28"/></w:rPr><w:t>${escapeXml(att.title)}</w:t></w:r></w:p>`;

    // Ensure all namespaces are available or standard w:/wp:/a:/pic: prefixes are used.
    // document.xml usually has these defined at root.
    const imageXml = `<w:p><w:pPr><w:jc w:val="center"/></w:pPr><w:r><w:drawing><wp:inline distT="0" distB="0" distL="0" distR="0"><wp:extent cx="${w}" cy="${h}"/><wp:effectExtent l="0" t="0" r="0" b="0"/><wp:docPr id="${maxId}" name="${escapeXml(att.name)}"/><wp:cNvGraphicFramePr><a:graphicFrameLocks noChangeAspect="1"/></wp:cNvGraphicFramePr><a:graphic><a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/picture"><pic:pic xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture"><pic:nvPicPr><pic:cNvPr id="${maxId}" name="${escapeXml(att.name)}"/><pic:cNvPicPr/></pic:nvPicPr><pic:blipFill><a:blip r:embed="${rId}"/><a:stretch><a:fillRect/></a:stretch></pic:blipFill><pic:spPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="${w}" cy="${h}"/></a:xfrm><a:prstGeom prst="rect"><a:avLst/></a:prstGeom></pic:spPr></pic:pic></a:graphicData></a:graphic></wp:inline></w:drawing></w:r></w:p>`;

    newContentXml += pageBreakXml + imageXml;
  }

  // 4. Inject into document.xml using String Replacement
  const lastSectPrIndex = docXml.lastIndexOf('<w:sectPr');
  const bodyEndIndex = docXml.lastIndexOf('</w:body>');

  if (lastSectPrIndex !== -1 && lastSectPrIndex < bodyEndIndex) {
    // Insert before the last sectPr
    docXml = docXml.slice(0, lastSectPrIndex) + newContentXml + docXml.slice(lastSectPrIndex);
  } else {
    // Fallback: Insert before </w:body> if no sectPr found (unlikely for valid docx but possible)
    if (bodyEndIndex !== -1) {
      docXml = docXml.slice(0, bodyEndIndex) + newContentXml + docXml.slice(bodyEndIndex);
    } else {
      throw new Error('Could not find </w:body> in document.xml');
    }
  }

  // 5. Update [Content_Types].xml
  const requiredExtensions = ['png', 'jpg', 'jpeg', 'gif', 'bmp'];
  const typesClosingTag = '</Types>';
  const typesInsertIndex = contentTypesXml.lastIndexOf(typesClosingTag);

  // Refined ContentTypes approach (Batch insert)


  let newDefaults = '';
  requiredExtensions.forEach(ext => {
    const regex = new RegExp(`Extension="${ext}"`, 'i');
    if (!regex.test(contentTypesXml)) {
      const contentType = `image/${ext === 'jpg' ? 'jpeg' : ext}`;
      newDefaults += `<Default Extension="${ext}" ContentType="${contentType}"/>`;
    }
  });

  if (newDefaults && typesInsertIndex !== -1) {
    contentTypesXml = contentTypesXml.slice(0, typesInsertIndex) + newDefaults + contentTypesXml.slice(typesInsertIndex);
  }

  // Save back to zip
  zip.file(docXmlPath, docXml);
  zip.file(relsPath, relsXml);
  zip.file(contentTypesPath, contentTypesXml);

  return zip;
}

function getImageDimensions(blob: Blob): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      reject(new Error('Could not load image for dimensions'));
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });
}

function escapeXml(unsafe: string): string {
  return unsafe.replace(/[<>&'"]/g, (c) => {
    switch (c) {
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '&': return '&amp;';
      case '\'': return '&apos;';
      case '"': return '&quot;';
      default: return c;
    }
  });
}
